import React, { useState, useEffect } from 'react';
import { Scissors, Sparkles, Heart, Clock, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FootprintIcon = () => <span className="text-2xl">🦶</span>;

export const ServicesPage = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  const icons = {
    Scissors: Scissors,
    Sparkles: Sparkles,
    Footprints: FootprintIcon,
    Heart: Heart,
  };

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await axios.get(`${API}/services`);
        // Filtrer uniquement les services actifs
        const activeServices = response.data.services.filter(s => s.isActive);
        setServices(activeServices);
      } catch (error) {
        console.error('Error fetching services:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchServices();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDF8F3] flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-[#C9A96E] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDF8F3]">
      {/* Hero */}
      <section className="relative py-24 bg-[#2C1810]">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <span className="text-[#C9A96E] font-medium uppercase tracking-wider text-sm">Nos Prestations</span>
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-serif font-bold text-white mt-4 mb-6">
            Services & Tarifs
          </h1>
          <div className="w-20 h-1 bg-gradient-to-r from-[#C9A96E] to-[#A67B5B] mx-auto mb-6" />
          <p className="text-[#D4C4B5] text-lg max-w-2xl mx-auto">
            Découvrez notre gamme complète de soins et services dédiés à la beauté afro. 
            Chaque prestation est réalisée avec expertise et passion.
          </p>
        </div>
      </section>

      {/* Services List */}
      <section className="py-20">
        <div className="max-w-5xl mx-auto px-6">
          {services.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-[#5D4037]">Aucun service disponible pour le moment.</p>
            </div>
          ) : (
            <Accordion type="single" collapsible className="space-y-6">
              {services.map((service, index) => {
                const IconComponent = icons[service.icon] || Sparkles;
                return (
                  <AccordionItem
                    key={service.id}
                    value={`service-${service.id}`}
                    className="bg-white rounded-2xl overflow-hidden shadow-lg border-none"
                  >
                    <AccordionTrigger className="hover:no-underline p-0">
                      <div className="flex items-center gap-6 w-full p-8 text-left">
                        <div className="w-24 h-24 rounded-xl overflow-hidden flex-shrink-0">
                          {service.image ? (
                            <img 
                              src={service.image.startsWith('/uploads') ? `${BACKEND_URL}${service.image}` : service.image} 
                              alt={service.category} 
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full bg-[#F5F0EB] flex items-center justify-center">
                              <IconComponent className="text-[#C9A96E]" size={40} />
                            </div>
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <IconComponent className="text-[#C9A96E]" size={24} />
                            <h3 className="text-2xl font-serif font-bold text-[#2C1810]">{service.category}</h3>
                          </div>
                          <p className="text-[#5D4037]">{service.description}</p>
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-8 pb-8">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                        {service.items && service.items.map((item, idx) => (
                          <div key={idx} className="bg-[#FDF8F3] rounded-xl p-6 hover:shadow-md transition-shadow">
                            <div className="flex justify-between items-start mb-3">
                              <h4 className="font-semibold text-xl md:text-2xl text-[#2C1810] leading-relaxed flex-1 pr-4">{item.name}</h4>
                              <span className="text-lg md:text-xl text-[#C9A96E] font-bold whitespace-nowrap">{item.price}</span>
                            </div>
                            <div className="flex items-center gap-2 text-base text-[#A67B5B]">
                              <Clock size={16} />
                              <span>{item.duration}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          )}

          <div className="mt-16 text-center">
            <Link 
              to="/reservation" 
              className="inline-block bg-[#C9A96E] text-white px-8 py-4 rounded-full font-medium hover:bg-[#A67B5B] transition-colors shadow-lg hover:shadow-xl"
            >
              Prendre rendez-vous
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
